import {
  HttpEvent,
  HttpEventType,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import {
  inject,
  makeStateKey,
  PLATFORM_ID,
  TransferState,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { of, tap } from 'rxjs';

export const transferStateInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  if (req.method !== 'GET') {
    return next(req);
  }

  const transferState = inject(TransferState);
  const platformId = inject(PLATFORM_ID);

  // Normalize the URL key by removing the protocol and host.
  // This allows http://back:3000/api/... to match /api/...
  // We use a regex to strip everything up to the first slash after the host.
  // If the URL is relative (e.g. /api/...), it stays as is.
  const relativeUrl = req.url.replace(/^https?:\/\/[^/]+/, '');

  // Create a unique key for TransferState. We prefix to avoid collisions with default cache.
  const STATE_KEY = makeStateKey<HttpResponse<unknown>>('TS:' + relativeUrl);

  if (transferState.hasKey(STATE_KEY)) {
    const response = transferState.get(STATE_KEY, null);
    if (response) {
      // Reconstruct HttpResponse because TransferState only stores the body/JSON
      return of(
        new HttpResponse({
          body: response.body,
          status: 200,
          statusText: 'OK',
          url: req.url,
        }),
      );
    }
  }

  return next(req).pipe(
    tap((event: HttpEvent<unknown>) => {
      if (
        event.type === HttpEventType.Response &&
        isPlatformServer(platformId) &&
        event.status === 200
      ) {
        // Store the simplified response (body only usually)
        // Note: HttpResponse is not serializable as-is, we usually just store the body.
        // However, restoring it on client requires wrapping it back in HttpResponse.
        transferState.set(STATE_KEY, { body: event.body } as any);
      }
    }),
  );
};
